import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  DateUI,
  Input,
  Label,
  LabelPill,
  PrimaryFullScreenSpinner,
  toast,
  UserProfile,
} from "@repo/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ChangeEvent, Suspense, useState } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/asset-balance-validations")({
  head: () => opsHead("Balance Validations"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();

  const [createFileMutation] = useMutation(
    graphql(`
      mutation CreateAssetBalanceValidationFile($input: CreateImportFileInput!) {
        createImportFile(input: $input) {
          importFile {
            id
            uploadUrl
          }
        }
      }
    `),
  );

  const [validateFileMutation] = useMutation(
    graphql(`
      mutation UploadAssetBalanceValidationFile($input: ValidateAssetBalanceFileInput!) {
        validateAssetBalanceFile(input: $input) {
          importFile {
            id
            status
          }
        }
      }
    `),
  );

  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileUpload = async () => {
    const file = selectedFile;
    if (!file) {
      return;
    }

    try {
      setUploading(true);

      const result = await createFileMutation({
        variables: {
          input: {
            fileName: file.name,
            kind: "ASSET_BALANCE_VALIDATION_FILE",
          },
        },
      });
      const importFile = result.data?.createImportFile?.importFile;
      if (!importFile) {
        throw new Error("expected import file in payload");
      }

      const uploadUrl = importFile.uploadUrl;
      if (!uploadUrl) {
        throw new Error("expected uploadUrl");
      }
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "text/csv",
        },
      });
      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
      }

      await validateFileMutation({
        variables: { input: { importFileId: importFile.id } },
      });

      void navigate({
        to: "/asset-balance-validations/$fileImportId",
        params: { fileImportId: importFile.id },
      });
    } catch (error) {
      toast({
        title: "Error validating file",
        description: String(error),
        kind: "error",
        autoHide: false,
      });
    } finally {
      setUploading(false);
    }
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    graphql(`
      query AdminAssetBalanceValidationFileList($after: String, $first: Int!, $order: ImportFilePageOrder!, $kinds: [FileImportKind!]) {
        importFiles(after: $after, first: $first, order: $order, kinds: $kinds) {
          pageInfo {
            endCursor
            hasNextPage
          }
          importFiles {
            id
            ... on AssetBalanceValidationImportFile {
              fileName
              uploadedAt
              effectiveAt
              status
              createdByUser {
                id
                ...UserProfileUI
              }
              assetBalanceValidations {
                id
                status
                validatedAt
                asset {
                  id
                  ... on FixedIncomeFundAsset {
                    product {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      }
    `),
    {
      variables: {
        after: null,
        first: 20,
        order: "DATE_DESC",
        kinds: ["ASSET_BALANCE_VALIDATION_FILE"],
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const files = (data?.importFiles.importFiles ?? []).flatMap((f) =>
    f.__typename === "AssetBalanceValidationImportFile" ? [f] : [],
  );

  const nextCursor = data?.importFiles?.pageInfo.hasNextPage
    ? (data.importFiles.pageInfo.endCursor ?? undefined)
    : undefined;

  return (
    <AdminPageContainer testId="admin-asset-balance-validations">
      <AdminPageHeader
        title="Balance validations"
        subtitle="Upload a provider balance file (SSGA 'Adviser Summary' or 'Accounts In Selected Fund' CSV) to validate ledger balances"
      />

      <div className="flex flex-col space-y-6">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await handleFileUpload();
          }}
          className="flex flex-col items-start space-y-2"
        >
          <Label htmlFor="csvFile">Upload balance file</Label>
          <div className="flex flex-row space-x-3">
            <div>
              <Input
                id="csvFile"
                type="file"
                accept=".csv"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0];
                  setSelectedFile(file ?? null);
                }}
              />
            </div>

            <Button
              variant="primary"
              type="submit"
              disabled={!selectedFile || uploading}
              loading={uploading}
            >
              {uploading ? "Validating..." : "Upload & validate"}
            </Button>
          </div>
        </form>

        <div>
          <DataTable
            data={files}
            manualSorting={true}
            manualPagination={true}
            maxMultiSortColCount={1}
            hidePagination={true}
            pageSize={10_000}
            className="overflow-hidden border"
            isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
            linkTo={(row) => `/asset-balance-validations/${row.original.id}`}
            columns={[
              {
                accessorKey: "uploadedAt",
                header: "Uploaded at",
                cell: ({ row }) => (
                  <DateUI
                    date={row.original.uploadedAt}
                    variant="DAY_MONTH_SHORT_TIME"
                  />
                ),
              },
              {
                accessorKey: "effectiveAt",
                header: "Effective at",
                cell: ({ row }) => {
                  if (!row.original.effectiveAt) {
                    return "-";
                  }
                  return (
                    <DateUI
                      date={row.original.effectiveAt}
                      variant="DAY_MONTH_SHORT_TIME"
                    />
                  );
                },
              },
              {
                id: "validatedAt",
                header: "Validated at",
                cell: ({ row }) => {
                  // validations are only the latest run, which all share a validatedAt
                  const validation = row.original.assetBalanceValidations?.[0];
                  if (!validation) {
                    return "-";
                  }
                  return (
                    <DateUI
                      date={validation.validatedAt}
                      variant="DAY_MONTH_SHORT_TIME"
                    />
                  );
                },
              },
              {
                id: "funds",
                header: "Fund",
                cell: ({ row }) => {
                  const fundNames = new Set(
                    (row.original.assetBalanceValidations ?? []).flatMap((v) =>
                      v.asset.__typename === "FixedIncomeFundAsset" &&
                      v.asset.product
                        ? [v.asset.product.name]
                        : [],
                    ),
                  );
                  return Array.from(fundNames).join(", ") || "-";
                },
              },
              {
                id: "status",
                header: "Status",
                cell: ({ row }) => {
                  const validations =
                    row.original.assetBalanceValidations ?? [];
                  if (validations.length === 0) {
                    return (
                      <LabelPill variant="neutral" label="Not validated" />
                    );
                  }
                  const matched = validations.filter(
                    (v) => v.status === "MATCHED",
                  ).length;
                  return (
                    <LabelPill
                      variant={
                        matched === validations.length ? "success" : "error"
                      }
                      label={`${matched}/${validations.length} accounts matched`}
                    />
                  );
                },
              },
              {
                id: "operator",
                header: "Operator",
                cell: ({ row }) =>
                  row.original.createdByUser ? (
                    <UserProfile {...row.original.createdByUser} />
                  ) : (
                    "-"
                  ),
              },
            ]}
          />
          <div className="mt-4 flex w-full flex-row justify-center">
            {nextCursor ? (
              <Button
                onClick={() => fetchMore({ variables: { after: nextCursor } })}
                variant="outline"
                loading={loading || networkStatus === NetworkStatus.fetchMore}
              >
                Load more
              </Button>
            ) : (
              files.length > 0 && (
                <p className="text-secondary text-sm">No more data</p>
              )
            )}
          </div>
        </div>
      </div>
    </AdminPageContainer>
  );
}
